import { createTheme } from "@mui/material/styles";

/**
 * Foyer CMS theme - derived from the "NendaPortal" Figma design language
 * (file Mtgy8uM4UJ0msnAe1zDFuu): periwinkle brand accent, slate ink/greys,
 * Inter type, soft 8px radii and flat (border-over-shadow) surfaces.
 *
 * Light/dark support: the colour palette is emitted as CSS variables so that
 * any `tokens.*` reference (component overrides, page `sx`, and SVG `style`)
 * follows the active mode automatically. The MUI palette itself is built from
 * real hex per mode so MUI's internal colour maths stays valid.
 */

type Vals = {
  brand: string;
  brandStrong: string;
  brandTint: string;
  brandTintSoft: string;
  /** Primary Selected - marked objects and selected rows. */
  brandSelected: string;
  /** Primary Active - active navigation, tabs, clearly active items. */
  brandActive: string;
  ink: string;
  inkMuted: string;
  inkFaint: string;
  surface: string;
  canvas: string;
  rowAlt: string;
  divider: string;
  success: string;
  successTint: string;
  error: string;
  errorTint: string;
  warning: string;
  warningTint: string;
  /**
   * The two data-visualisation series hues (POS-12).
   *
   * Deliberately their own tokens rather than `brand` and `success`: a chart
   * series is an identity channel, and reusing the accent (which also paints
   * every button and selected row) or a status colour (which is reserved for
   * good/bad) makes a bar mean two things at once. Two slots is the whole
   * palette, because the funnel has exactly two halves and a hue per column
   * would be decoration.
   *
   * `vizAttention` is what the television counted; `vizMoney` is what somebody
   * at reception accepted. Both were validated as a categorical pair against
   * their own surface (lightness band, chroma floor, CVD separation, contrast):
   * worst adjacent CVD ΔE 25.2 light / 26.1 dark, normal-vision 30.9 / 29.9,
   * both ≥ 3:1 on their surface. Re-run the check before changing either.
   */
  vizAttention: string;
  vizMoney: string;
  /** Card lift. Two layers: a tight contact shadow plus a wide, negative-spread
   *  ambient one. Dark mode needs pure black at much higher alpha - a slate
   *  shadow over a near-black canvas reads as mud, not depth. */
  shadowCard: string;
};

const lightVals: Vals = {
  // The five primary steps the design system defines, and what each is for:
  //   #5C79DD primary buttons, links, focus
  //   #4A66C8 hover/pressed on a primary CTA
  //   #DEE4F8 active navigation, tabs, clearly active items
  //   #E7EBFA selected objects and rows
  //   #EFF2FC hover on rows, cards, filters, dropdowns
  brand: "#5C79DD",
  brandStrong: "#4A66C8",
  brandTint: "#E7EBFA",
  brandTintSoft: "#EFF2FC",
  brandSelected: "#E7EBFA",
  brandActive: "#DEE4F8",
  ink: "#0F172A",
  inkMuted: "#64748B",
  inkFaint: "#8A98A7",
  surface: "#FFFFFF",
  canvas: "#F8FAFC",
  rowAlt: "#F1F5F9",
  divider: "#E2E8F0",
  success: "#2E7D32",
  successTint: "#E7F4E8",
  error: "#D32F2F",
  errorTint: "#FCEBEB",
  warning: "#B26A00",
  warningTint: "#FBF0DD",
  // The periwinkle sits inside the light AND the dark lightness band, so the
  // attention hue is one step in both modes; only the money hue is re-stepped.
  vizAttention: "#5C79DD",
  vizMoney: "#EB6834",
  shadowCard:
    "0 1px 3px rgba(16,20,30,0.05), 0 10px 30px -20px rgba(16,20,30,0.35)",
};

const darkVals: Vals = {
  brand: "#8AA0EE",
  brandStrong: "#6E8BFF",
  brandTint: "rgba(124,147,232,0.18)",
  brandTintSoft: "rgba(124,147,232,0.10)",
  brandSelected: "rgba(124,147,232,0.18)",
  brandActive: "rgba(124,147,232,0.26)",
  ink: "#E6EAF2",
  inkMuted: "#9AA6B8",
  inkFaint: "#6E7A8C",
  surface: "#171B23",
  canvas: "#0E1116",
  rowAlt: "#1E232D",
  divider: "#2A313D",
  success: "#5BBF62",
  successTint: "rgba(91,191,98,0.16)",
  error: "#F06B6B",
  errorTint: "rgba(240,107,107,0.16)",
  warning: "#E0A23B",
  warningTint: "rgba(224,162,59,0.16)",
  vizAttention: "#5C79DD",
  vizMoney: "#D95926",
  shadowCard: "0 1px 3px rgba(0,0,0,0.40), 0 10px 30px -20px rgba(0,0,0,0.90)",
};

/**
 * Token palette exposed to components - every value is a CSS variable so it
 * resolves to whichever mode is active (see CssBaseline `:root` below). Use
 * these in `sx`, CSS, and SVG `style` (NOT as raw SVG presentation attributes).
 */
export const tokens = {
  brand: "var(--fy-brand)",
  brandStrong: "var(--fy-brand-strong)",
  brandTint: "var(--fy-brand-tint)",
  brandTintSoft: "var(--fy-brand-tint-soft)",
  brandSelected: "var(--fy-brand-selected)",
  brandActive: "var(--fy-brand-active)",
  ink: "var(--fy-ink)",
  inkMuted: "var(--fy-ink-muted)",
  inkFaint: "var(--fy-ink-faint)",
  surface: "var(--fy-surface)",
  canvas: "var(--fy-canvas)",
  rowAlt: "var(--fy-row-alt)",
  divider: "var(--fy-divider)",
  success: "var(--fy-success)",
  successTint: "var(--fy-success-tint)",
  error: "var(--fy-error)",
  errorTint: "var(--fy-error-tint)",
  warning: "var(--fy-warning)",
  warningTint: "var(--fy-warning-tint)",
  /** Chart series 1 — what the television counted. See `Vals.vizAttention`. */
  vizAttention: "var(--fy-viz-attention)",
  /** Chart series 2 — what reception accepted. Money, never impressions. */
  vizMoney: "var(--fy-viz-money)",
  shadowCard: "var(--fy-shadow-card)",
} as const;

function cssVars(v: Vals): Record<string, string> {
  return {
    "--fy-brand": v.brand,
    "--fy-brand-strong": v.brandStrong,
    "--fy-brand-tint": v.brandTint,
    "--fy-brand-tint-soft": v.brandTintSoft,
    "--fy-brand-selected": v.brandSelected,
    "--fy-brand-active": v.brandActive,
    "--fy-ink": v.ink,
    "--fy-ink-muted": v.inkMuted,
    "--fy-ink-faint": v.inkFaint,
    "--fy-surface": v.surface,
    "--fy-canvas": v.canvas,
    "--fy-row-alt": v.rowAlt,
    "--fy-divider": v.divider,
    "--fy-success": v.success,
    "--fy-success-tint": v.successTint,
    "--fy-error": v.error,
    "--fy-error-tint": v.errorTint,
    "--fy-warning": v.warning,
    "--fy-warning-tint": v.warningTint,
    "--fy-viz-attention": v.vizAttention,
    "--fy-viz-money": v.vizMoney,
    "--fy-shadow-card": v.shadowCard,
  };
}

const FONT_SANS = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(", ");

export type ThemeMode = "light" | "dark";

export function makeTheme(mode: ThemeMode) {
  const v = mode === "dark" ? darkVals : lightVals;
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: v.brand,
        light: v.brandTint,
        dark: isDark ? "#6E8BFF" : "#4259C4",
        contrastText: "#FFFFFF",
      },
      secondary: { main: v.brandStrong, contrastText: "#FFFFFF" },
      // NB: don't set `light` to the translucent tint - MUI uses palette
      // `light` as the standard-Alert text colour in dark mode. Tints are
      // applied via tokens.*Tint in the component overrides instead.
      error: { main: v.error },
      success: { main: v.success },
      warning: { main: v.warning },
      text: { primary: v.ink, secondary: v.inkMuted, disabled: v.inkFaint },
      // Every hover/selected surface in the app resolves to the design
      // system's tints instead of MUI's grey defaults.
      action: {
        hover: v.brandTintSoft,
        selected: v.brandSelected,
        focus: v.brandSelected,
      },
      divider: v.divider,
      background: { default: v.canvas, paper: v.surface },
    },

    shape: { borderRadius: 8 },

    typography: {
      fontFamily: FONT_SANS,
      h1: { fontWeight: 700, fontSize: "2rem", letterSpacing: "-0.02em" },
      h2: { fontWeight: 700, fontSize: "1.625rem", letterSpacing: "-0.02em" },
      h3: { fontWeight: 700, fontSize: "1.375rem", letterSpacing: "-0.01em" },
      h4: { fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.01em" },
      h5: { fontWeight: 600, fontSize: "1.125rem" },
      h6: { fontWeight: 600, fontSize: "1rem" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600, color: tokens.inkMuted },
      body1: { fontSize: "0.9375rem" },
      body2: { fontSize: "0.875rem" },
      caption: { fontSize: "0.75rem", color: tokens.inkMuted },
      button: { textTransform: "none", fontWeight: 600 },
      overline: {
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        fontWeight: 600,
        fontSize: "0.6875rem",
        color: tokens.inkFaint,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": cssVars(v),
          body: {
            backgroundColor: tokens.canvas,
            color: tokens.ink,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "*:focus-visible": {
            outline: `2px solid ${tokens.brand}`,
            outlineOffset: 2,
            borderRadius: 4,
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // "Alla knappar har radius 8px. Standard höjd 40 px (Medium).
          //  Ikon och text har 8 px gap." - Nenda Design System v1.0.
          root: { borderRadius: 8, paddingInline: 16, minHeight: 40, gap: 8 },
          contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
          sizeSmall: { paddingInline: 12, minHeight: 32 },
          sizeLarge: { paddingInline: 20, minHeight: 48 },
          startIcon: { marginRight: 0, marginLeft: 0 },
          endIcon: { marginLeft: 0, marginRight: 0 },
        },
      },

      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: {
            borderColor: tokens.divider,
            boxShadow: tokens.shadowCard,
          },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${tokens.divider}`,
            borderRadius: 12,
            backgroundImage: "none",
            boxShadow: tokens.shadowCard,
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
          },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: {
            backgroundColor: tokens.surface,
            color: tokens.ink,
            borderBottom: `1px solid ${tokens.divider}`,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: { borderColor: tokens.divider, backgroundImage: "none" },
        },
      },

      MuiTextField: { defaultProps: { size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.divider,
            },
          },
        },
      },
      MuiSelect: { defaultProps: { size: "small" } },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 600 },
          sizeSmall: { height: 24 },
          colorSuccess: {
            backgroundColor: tokens.successTint,
            color: tokens.success,
          },
          colorError: {
            backgroundColor: tokens.errorTint,
            color: tokens.error,
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: { "& .MuiTableCell-head": { backgroundColor: tokens.surface } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: tokens.divider },
          head: {
            fontWeight: 600,
            color: tokens.inkMuted,
            fontSize: "0.8125rem",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          hover: { "&:hover": { backgroundColor: tokens.brandTintSoft } },
        },
      },

      /**
       * Dialogs go full-bleed on a phone.
       *
       * There are 66 of them and not one passes `fullScreen`, so on a handset
       * every one rendered as a cramped card floating inside an already narrow
       * viewport - a form squeezed twice, once by the margins and once by the
       * `maxWidth` the call site asked for on the assumption of a desktop.
       *
       * Done here rather than at the call sites deliberately: `fullScreen` is a
       * PROP, so honouring it everywhere would mean a `useMediaQuery` in 66
       * components and a 67th that forgets. A style override needs no component
       * to know it exists, and a dialog added next year inherits it.
       *
       * It is a pure presentation change - the Dialog is still a Dialog, still
       * modal, still focus-trapped - so nothing that opens one has to care.
       */
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${tokens.divider}`,
            "@media (max-width:599.95px)": {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              // `maxHeight` rather than `height`: a short dialog should stay
              // short. Only the tall ones need the whole screen.
              maxHeight: "100%",
              borderRadius: 0,
              border: "none",
            },
          },
          // `scroll="paper"` (the default) caps the paper at calc(100% - 64px)
          // via this class, which would leave a band of backdrop under a
          // full-bleed sheet.
          paperScrollPaper: {
            "@media (max-width:599.95px)": { maxHeight: "100%" },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 700, fontSize: "1.25rem" } },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.ink,
            color: tokens.surface,
            fontSize: "0.75rem",
            borderRadius: 6,
            paddingInline: 10,
            paddingBlock: 6,
          },
          arrow: { color: tokens.ink },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            "&.Mui-selected": {
              backgroundColor: tokens.brand,
              color: "#FFFFFF",
              "& .MuiListItemIcon-root": { color: "#FFFFFF" },
              "&:hover": { backgroundColor: tokens.brand },
            },
            "&:hover": { backgroundColor: tokens.brandTintSoft },
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${tokens.divider}`,
            boxShadow:
              "0 4px 12px rgba(15,23,42,0.12), 0 1px 3px rgba(15,23,42,0.10)",
          },
        },
      },

      MuiTab: {
        styleOverrides: { root: { textTransform: "none", fontWeight: 600 } },
      },

      MuiSwitch: {
        styleOverrides: {
          switchBase: { "&.Mui-checked": { color: tokens.brand } },
          track: {
            ".Mui-checked.Mui-checked + &": { backgroundColor: tokens.brand },
          },
        },
      },

      MuiLink: {
        defaultProps: { underline: "hover" },
        styleOverrides: { root: { color: tokens.brand, fontWeight: 500 } },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: tokens.divider } },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10 },
          standardSuccess: {
            backgroundColor: tokens.successTint,
            color: tokens.success,
            "& .MuiAlert-icon": { color: tokens.success },
          },
          standardError: {
            backgroundColor: tokens.errorTint,
            color: tokens.error,
            "& .MuiAlert-icon": { color: tokens.error },
          },
          standardWarning: {
            backgroundColor: tokens.warningTint,
            color: tokens.warning,
            "& .MuiAlert-icon": { color: tokens.warning },
          },
          standardInfo: {
            backgroundColor: tokens.brandTint,
            color: tokens.brand,
            "& .MuiAlert-icon": { color: tokens.brand },
          },
        },
      },
    },
  });
}

export const theme = makeTheme("light");

export default theme;
